import { useCallback, useEffect, useRef, useState } from 'react';
import {
  administrativeExpiry,
  normalizeAdministrativeMoney,
  parseAdministrativeProxy,
} from '@snaphire/contracts';
import { getObject, type Schema } from '../api/client';
import { ApiError } from '../api/session';
import { Button, Empty, ErrorState, Field, Icon, Search, StatTabs, Table, Tabs } from '../ui';
import {
  administrativeIntegrationsReady,
  AdminConfirm,
  AdminForm,
  AdminLink,
  AdminSelect,
  InfoRows,
  Missing,
  QueryState,
  dateLabel,
  dateTimeLabel,
  kindLabels,
  moneyLabel,
  nullable,
  text,
  today,
  useAdminAction,
  useAdminList,
  useAdminNavigate,
  useAdminQuery,
  useAdministrative,
  type Resource,
} from './common';
import { AdministrativeFilters, AdministrativeList, CompactFilter } from './list-layout';

const lifecyclePaths = {
  archive: '/api/v1/administrative/resources/{id}/archive',
  unarchive: '/api/v1/administrative/resources/{id}/unarchive',
  trash: '/api/v1/administrative/resources/{id}/trash',
  restore: '/api/v1/administrative/resources/{id}/restore',
  purge: '/api/v1/administrative/resources/{id}/purge',
} as const;
function expiryLabel(row: Resource, currentDay: string) {
  const expiry = administrativeExpiry(row.paidUntil, currentDay);
  return expiry.state === 'UNKNOWN'
    ? 'Срок неизвестен'
    : expiry.state === 'TODAY'
      ? 'Истекает сегодня'
      : expiry.state === 'EXPIRED'
        ? `Просрочено: ${Math.abs(expiry.days!)} дн.`
        : `Осталось: ${expiry.days} дн.`;
}
function validIPAddress(value: string) {
  if (/^\d+\.\d+\.\d+\.\d+$/.test(value))
    return value.split('.').every((part) => /^(0|[1-9]\d{0,2})$/.test(part) && Number(part) <= 255);
  if (!value.includes(':')) return false;
  const [address, zone, ...extra] = value.split('%');
  if (extra.length || (zone !== undefined && !/^[a-zA-Z0-9_.:-]+$/.test(zone))) return false;
  try {
    return new URL(`http://[${address}]/`).hostname.startsWith('[');
  } catch {
    return false;
  }
}
function validResourceHost(value: string) {
  if (validIPAddress(value.replace(/^\[|\]$/g, ''))) return true;
  if (!value || /[\s/:@?#]/.test(value)) return false;
  try {
    const hostname = new URL(`http://${value}`).hostname;
    return (
      hostname.length <= 253 &&
      hostname
        .split('.')
        .every((part) => /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(part))
    );
  } catch {
    return false;
  }
}
export function AdministrativeResources({
  sheetId,
  state = 'ACTIVE',
  initialKind = '',
  initialProvider = '',
  initialProject = '',
  initialCurrency = '',
}: {
  sheetId?: string;
  state?: 'ACTIVE' | 'ARCHIVED' | 'TRASH';
  initialKind?: string;
  initialProvider?: string;
  initialProject?: string;
  initialCurrency?: string;
}) {
  const me = useAdministrative();
  const action = useAdminAction();
  const sheet = me.sheets.find((item) => item.id === sheetId);
  const [filters, setFilters] = useState<Record<string, string>>({
    ...(sheetId ? { sheetId } : {}),
    state,
    ...(initialKind ? { kind: initialKind } : {}),
    ...(initialProvider ? { providerAccountId: initialProvider } : {}),
    ...(initialProject ? { projectId: initialProject } : {}),
    ...(initialCurrency ? { currency: initialCurrency } : {}),
  });
  const [selected, setSelected] = useState<string[]>([]);
  const [bulk, setBulk] = useState('');
  const [projectId, setProject] = useState('');
  const [responsible, setResponsible] = useState('');
  const [savedName, setSavedName] = useState('');
  const [saveDialog, setSaveDialog] = useState(false);
  const list = useAdminList<Resource>(
    state === 'TRASH' ? '/api/v1/administrative/trash' : '/api/v1/administrative/resources',
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
  );
  const counts = (list.data?.pages[0] as Schema<'AdminResourcePage'> | undefined)?.counts;
  const saved = useAdminQuery<Schema<'AdminSavedFilterPage'>>(
    '/api/v1/administrative/saved-filters',
  );
  const members = useAdminQuery<Schema<'AdminMemberPage'>>(
    '/api/v1/administrative/members',
    {},
    {},
    me.allowedActions.includes('members.read'),
  );
  const update = (key: string, value: string) => {
    setSelected([]);
    setFilters((previous) => ({ ...previous, [key]: value }));
  };
  if (sheetId && !sheet) return <Missing />;
  const createAllowed = sheet
    ? sheet.creationAllowedActions.includes('resource.create')
    : me.allowedActions.includes('resource.create');
  const rows = list.items;
  const chosen = rows.filter((row) => selected.includes(row.id));
  const financeVisible = rows.some((row) => row.capabilities.financeRead);
  return (
    <>
      <section className="panel data-panel admin-list admin-resource-list">
        <StatTabs
          selected={sheet ? sheet.kind : filters.kind || 'ALL'}
          onChange={(value) => update('kind', value === 'ALL' ? '' : value)}
          tabs={[
            ...(!sheet
              ? [
                  {
                    value: 'ALL',
                    label: 'Всего',
                    count: counts?.reduce((sum, row) => sum + row.total, 0) ?? '…',
                  },
                ]
              : []),
            ...Object.entries(kindLabels)
              .filter(([kind]) => !sheet || kind === sheet.kind)
              .map(([value, label]) => {
                const count = counts?.find((row) => row.kind === value);
                return {
                  value,
                  label: sheet?.name || label,
                  count: count?.total,
                };
              }),
          ]}
        />
        <div className="table-toolbar admin-resource-toolbar">
          <Search
            value={filters.search || ''}
            onChange={(value) => update('search', value)}
            placeholder="Поиск ресурсов"
          />
          <Button
            className={filters.mine === 'true' ? 'selected' : ''}
            onClick={() => update('mine', filters.mine === 'true' ? '' : 'true')}
          >
            Только мои
          </Button>
          <AdministrativeFilters
            label="Фильтры ресурсов"
            fields={[
              { key: 'projectId', label: 'Проект', allLabel: 'Все проекты', options: me.projects },
              {
                key: 'providerAccountId',
                label: 'Провайдер',
                allLabel: 'Все провайдеры',
                options: me.providers,
              },
              {
                key: 'statusId',
                label: 'Статус',
                allLabel: 'Все статусы',
                options: me.dictionaries.filter((item) => item.kind === 'STATUS'),
              },
              {
                key: 'relevanceId',
                label: 'Актуальность',
                allLabel: 'Любая актуальность',
                options: me.dictionaries.filter((item) => item.kind === 'RELEVANCE'),
              },
              { key: 'from', label: 'Дата окончания: с', type: 'date', maxKey: 'to' },
              { key: 'to', label: 'Дата окончания: по', type: 'date', minKey: 'from' },
              ...(me.capabilities.financeRead || financeVisible
                ? [
                    {
                      key: 'currency',
                      label: 'Валюта',
                      allLabel: 'Все валюты',
                      options: ['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id })),
                    },
                  ]
                : []),
            ]}
            values={filters}
            validate={(values) =>
              values.from && values.to && values.from > values.to
                ? 'Конец периода не может быть раньше начала.'
                : null
            }
            onApply={(values) => {
              setSelected([]);
              setFilters((previous) => ({
                ...previous,
                ...Object.fromEntries(
                  [
                    'projectId',
                    'providerAccountId',
                    'statusId',
                    'relevanceId',
                    'currency',
                    'from',
                    'to',
                  ].map((key) => [key, values[key] || '']),
                ),
                ...(previous.sort === 'cost' && !values.currency ? { sort: 'name' } : {}),
              }));
            }}
          />
          <CompactFilter
            label="Сохранённые"
            allLabel="Сохранённые"
            options={[
              { id: '__MANAGE_FILTERS__', name: 'Сохранить и управлять фильтрами' },
              ...(saved.data?.items || []),
            ]}
            onChange={(id) => {
              if (id === '__MANAGE_FILTERS__') {
                action.setError(null);
                setSaveDialog(true);
                return;
              }
              const item = saved.data?.items.find((row) => row.id === id);
              if (item) {
                setSelected([]);
                setFilters(
                  Object.fromEntries(
                    Object.entries({ ...item.filters, state, ...(sheetId ? { sheetId } : {}) }).map(
                      ([key, value]) => [key, String(value)],
                    ),
                  ),
                );
              }
            }}
          />
          <span className="toolbar-spacer" />
          {state === 'ACTIVE' && createAllowed && (
            <AdminLink
              className="pill admin-add-action"
              path={`resources/new${sheetId ? `?sheetId=${sheetId}` : ''}`}
            >
              <Icon name="Plus" size={16} />
              Добавить ресурс
            </AdminLink>
          )}
          {sheet?.allowedActions.includes('sheet.edit') && (
            <AdminLink className="pill" path={`sheets/${sheet.id}/edit`}>
              <Icon name="Controls" size={16} />
              Настройки листа
            </AdminLink>
          )}
          {state === 'ACTIVE' && administrativeIntegrationsReady && (
            <AdminLink className="pill" path={`import${sheetId ? `?sheetId=${sheetId}` : ''}`}>
              Импорт и экспорт
            </AdminLink>
          )}
        </div>
        <QueryState query={list} />
        <Table
          rows={rows}
          hasMore={list.hasNextPage}
          loadMore={() => void list.fetchNextPage()}
          onSort={(sort, direction) => {
            if (sort === 'cost' && !filters.currency) {
              action.setError(new Error('Для сортировки стоимости выберите валюту в фильтрах.'));
              return;
            }
            if (['name', 'paidUntil', 'cost'].includes(sort))
              setFilters((value) => ({ ...value, sort, direction }));
          }}
          columns={[
            {
              key: 'select',
              label: '',
              sortable: false,
              filter: false,
              width: 40,
              render: (row) => (
                <input
                  type="checkbox"
                  aria-label={`Выбрать ${row.name}`}
                  checked={selected.includes(row.id)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked
                        ? [...selected, row.id]
                        : selected.filter((id) => id !== row.id),
                    )
                  }
                />
              ),
            },
            {
              key: 'name',
              label: 'Ресурс',
              filter: false,
              render: (row) => <AdminLink path={`resources/${row.id}`}>{row.name}</AdminLink>,
            },
            {
              key: 'kind',
              label: 'Тип',
              sortable: false,
              filter: false,
              render: (row) => kindLabels[row.kind],
            },
            {
              key: 'projectId',
              label: 'Проект',
              sortable: false,
              filter: false,
              render: (row) =>
                me.projects.find((item) => item.id === row.projectId)?.name || 'Не указан',
            },
            {
              key: 'paidUntil',
              label: 'Оплачен до',
              filter: false,
              render: (row) => (
                <span className="admin-expiry">
                  <span>{dateLabel(row.paidUntil)}</span>
                  <small
                    className={
                      administrativeExpiry(row.paidUntil, today(me)).state === 'EXPIRED'
                        ? 'admin-overdue'
                        : 'muted'
                    }
                  >
                    {expiryLabel(row, today(me))}
                  </small>
                </span>
              ),
            },
            {
              key: 'statusId',
              label: 'Рабочий статус',
              sortable: false,
              filter: false,
              render: (row) =>
                me.dictionaries.find((item) => item.id === row.statusId)?.name || 'Не указан',
            },
            ...(financeVisible
              ? [
                  {
                    key: 'cost',
                    label: 'Стоимость за период',
                    filter: false,
                    render: (row: Resource) =>
                      row.capabilities.financeRead
                        ? row.priceKind === 'FREE'
                          ? 'Бесплатно'
                          : moneyLabel(row.cost, row.currency)
                        : 'Доступ ограничен',
                  },
                ]
              : []),
            ...(sheet?.customColumns.map((column) => ({
              key: column.key,
              label: column.name,
              sortable: false,
              filter: false,
              render: (row: Resource) => row.customValues[column.key] || '—',
            })) || []),
          ]}
        />
        {selected.length > 0 && (
          <div className="admin-toolbar">
            <span>Выбрано: {selected.length}</span>
            {['UPDATE', 'ARCHIVE', 'TRASH', 'RENEW', 'CHECK']
              .filter((operation) => administrativeIntegrationsReady || operation !== 'CHECK')
              .filter((operation) =>
                chosen.every(
                  (row) =>
                    row.allowedActions.includes(
                      (
                        {
                          UPDATE: 'resource.edit',
                          ARCHIVE: 'resource.archive',
                          TRASH: 'resource.delete',
                          RENEW: 'finance.edit',
                          CHECK: 'proxy.check',
                        } as Record<string, string>
                      )[operation]!,
                    ) &&
                    (operation !== 'CHECK' || row.kind === 'PROXY'),
                ),
              )
              .map((operation) => (
                <Button
                  key={operation}
                  disabled={action.pending}
                  onClick={() => setBulk(operation)}
                >
                  {
                    (
                      {
                        UPDATE: 'Изменить',
                        ARCHIVE: 'Архивировать',
                        TRASH: 'В корзину',
                        RENEW: 'Продлить',
                        CHECK: 'Проверить прокси',
                      } as Record<string, string>
                    )[operation]
                  }
                </Button>
              ))}
          </div>
        )}
        {state === 'TRASH' && (
          <p className="muted admin-retention-note">
            Автоматическое удаление через 30 дней · история оплат сохраняется
          </p>
        )}
      </section>
      {saveDialog && (
        <AdminConfirm
          title="Сохранённые фильтры"
          action={action}
          onClose={() => setSaveDialog(false)}
          label="Сохранить фильтр"
          onConfirm={async () => {
            if (!savedName.trim()) {
              action.setError(new Error('Укажите название фильтра'));
              return;
            }
            if (
              await action.run('POST', '/api/v1/administrative/saved-filters', {
                name: savedName,
                filters: Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
              })
            ) {
              setSavedName('');
              setSaveDialog(false);
            }
          }}
        >
          <Field
            label="Название нового фильтра"
            value={savedName}
            onChange={(event) => setSavedName(event.target.value)}
            maxLength={200}
          />
          {saved.data?.items.map((item) => (
            <SavedFilterRow key={item.id} item={item} action={action} />
          ))}
        </AdminConfirm>
      )}
      {action.error && !bulk && !saveDialog && <ErrorState error={action.error} />}
      {bulk && (
        <BulkForm
          chosen={chosen}
          operation={bulk}
          projectId={projectId}
          setProject={setProject}
          responsible={responsible}
          setResponsible={setResponsible}
          members={members.data?.items || []}
          action={action}
          onClose={() => setBulk('')}
          onDone={() => {
            setBulk('');
            setSelected([]);
          }}
        />
      )}
    </>
  );
}
function SavedFilterRow({
  item,
  action,
}: {
  item: Schema<'AdminSavedFilter'>;
  action: ReturnType<typeof useAdminAction>;
}) {
  const [draft, setDraft] = useState<{ name: string; version: number } | null>(null);
  return (
    <div className="admin-saved-view">
      {draft ? (
        <>
          <Field
            label={`Новое название фильтра ${item.name}`}
            value={draft.name}
            maxLength={200}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
          <Button
            disabled={action.pending}
            onClick={async () => {
              if (!draft.name.trim()) {
                action.setError(new Error('Укажите название фильтра'));
                return;
              }
              const body: Schema<'AdminSavedFilterPatch'> = {
                name: draft.name.trim(),
                version: draft.version,
              };
              if (
                await action.run(
                  'PATCH',
                  '/api/v1/administrative/saved-filters/{id}',
                  body,
                  { id: item.id },
                  'Фильтр переименован',
                )
              )
                setDraft(null);
            }}
          >
            Сохранить название
          </Button>
          <Button disabled={action.pending} onClick={() => setDraft(null)}>
            Отменить переименование
          </Button>
        </>
      ) : (
        <>
          <span>{item.name}</span>
          <Button
            disabled={action.pending}
            onClick={() => {
              action.setError(null);
              setDraft({ name: item.name, version: item.version });
            }}
          >
            Переименовать
          </Button>
          <Button
            disabled={action.pending}
            onClick={() =>
              void action.run(
                'DELETE',
                '/api/v1/administrative/saved-filters/{id}',
                { version: item.version },
                { id: item.id },
                'Фильтр удалён',
              )
            }
          >
            Удалить
          </Button>
        </>
      )}
    </div>
  );
}
function BulkForm({
  chosen,
  operation,
  projectId,
  setProject,
  responsible,
  setResponsible,
  members,
  action,
  onClose,
  onDone,
}: {
  chosen: Resource[];
  operation: string;
  projectId: string;
  setProject: (value: string) => void;
  responsible: string;
  setResponsible: (value: string) => void;
  members: Schema<'AdminMember'>[];
  action: ReturnType<typeof useAdminAction>;
  onClose: () => void;
  onDone: () => void;
}) {
  const me = useAdministrative();
  const navigate = useAdminNavigate();
  const [paymentDate, setPaymentDate] = useState(today(me));
  const [paidUntil, setPaidUntil] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [changeProject, setChangeProject] = useState(false);
  const [changeResponsible, setChangeResponsible] = useState(false);
  return (
    <AdminConfirm
      title={`Массовая операция: ${chosen.length} ресурсов`}
      action={action}
      onClose={onClose}
      onConfirm={async () => {
        if (operation === 'CHECK') {
          const job = await action.run<Schema<'AdminIntegrationJob'>>(
            'POST',
            '/api/v1/administrative/integrations/proxy-checks',
            { resourceIds: chosen.map((row) => row.id), operationKey: crypto.randomUUID() },
            {},
            'Проверка поставлена в очередь',
          );
          if (job) {
            onDone();
            await navigate(`jobs/${job.id}`);
          }
          return;
        }
        const patch = {
          ...(changeProject ? { projectId: projectId || null } : {}),
          ...(changeResponsible ? { responsibleAccountId: responsible || null } : {}),
        };
        if (operation === 'UPDATE' && !changeProject && !changeResponsible) {
          action.setError(new Error('Выберите поля для изменения'));
          return;
        }
        let renewal;
        try {
          if (operation === 'RENEW')
            renewal = {
              paymentDate,
              paidUntil,
              amount: normalizeAdministrativeMoney(amount),
              currency,
              operationKey: crypto.randomUUID(),
            };
        } catch (error) {
          action.setError(error as Error);
          return;
        }
        if (
          await action.run('POST', '/api/v1/administrative/resources/bulk', {
            items: chosen.map(({ id, version }) => ({ id, version })),
            action: operation,
            ...(operation === 'UPDATE' ? { patch } : {}),
            ...(renewal ? { renewal } : {}),
          })
        )
          onDone();
      }}
    >
      <p>Операция применяется к выбранным записям после проверки всех прав и версий.</p>
      {operation === 'UPDATE' && (
        <>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={changeProject}
              onChange={(e) => setChangeProject(e.target.checked)}
            />
            Изменить проект
          </label>
          <AdminSelect
            label="Проект"
            options={me.projects}
            value={projectId}
            onChange={setProject}
          />
          <label className="admin-check">
            <input
              type="checkbox"
              checked={changeResponsible}
              onChange={(e) => setChangeResponsible(e.target.checked)}
            />
            Изменить ответственного
          </label>
          <AdminSelect
            label="Ответственный"
            options={members.map((item) => ({ id: item.accountId, name: item.login }))}
            value={responsible}
            onChange={setResponsible}
          />
        </>
      )}
      {operation === 'RENEW' && (
        <>
          <Field
            label="Дата фактической оплаты"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <Field
            label="Сумма на каждый ресурс"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <AdminSelect
            label="Валюта"
            options={['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id }))}
            value={currency}
            onChange={setCurrency}
          />
          <Field
            label="Новый срок для всех ресурсов"
            type="date"
            value={paidUntil}
            onChange={(e) => setPaidUntil(e.target.value)}
          />
        </>
      )}
    </AdminConfirm>
  );
}
export function AdministrativeResourceForm({ id, sheetId }: { id: string; sheetId?: string }) {
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const creating = id === 'new';
  const [reset, setReset] = useState(0);
  const query = useAdminQuery<Resource>(
    '/api/v1/administrative/resources/{id}',
    { id },
    {},
    !creating,
  );
  const resource = query.data;
  if (query.error instanceof ApiError && [403, 404].includes(query.error.status))
    return <QueryState query={query} />;
  if (!creating && !resource) return <QueryState query={query} />;
  if (resource && !resource.allowedActions.includes('resource.edit'))
    return <Empty title="Изменение ресурса недоступно" />;
  return (
    <ResourceEditor
      key={`${id}:${reset}`}
      resource={resource}
      initialSheet={sheetId}
      action={action}
      onReload={async () => {
        const result = await query.refetch();
        if (result.error || !result.data) return;
        action.setError(null);
        setReset((previous) => previous + 1);
      }}
      onDone={(resource) => navigate(`resources/${resource.id}`)}
    />
  );
}
function ResourceEditor({
  resource: latestResource,
  initialSheet,
  action,
  onReload,
  onDone,
}: {
  resource?: Resource;
  initialSheet?: string;
  action: ReturnType<typeof useAdminAction>;
  onReload: () => Promise<void>;
  onDone: (resource: Resource) => Promise<unknown>;
}) {
  const me = useAdministrative();
  const [resource] = useState(latestResource);
  const [sheetId, setSheetId] = useState(resource?.sheetId || initialSheet || '');
  const sheet = me.sheets.find((item) => item.id === sheetId);
  const caps = latestResource?.capabilities || {
    financeEdit: Boolean(sheet?.creationAllowedActions.includes('finance.edit')),
    connectionEdit: Boolean(sheet?.creationAllowedActions.includes('connection.edit')),
  };
  const [metadata, setMetadata] = useState(resource?.metadata || {});
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [replaceCredentials, setReplaceCredentials] = useState(!resource);
  const [clearCredentials, setClearCredentials] = useState(false);
  const [proxyInput, setProxyInput] = useState('');
  const [proxyError, setProxyError] = useState<Error | null>(null);
  const [priceKind, setPriceKind] = useState(resource?.priceKind || 'UNKNOWN');
  const [replaceFinance, setReplaceFinance] = useState(false);
  const readableFinance = Boolean(
    resource?.capabilities.financeRead && latestResource?.capabilities.financeRead,
  );
  const editFinance = caps.financeEdit && (!resource || readableFinance || replaceFinance);
  const members = useAdminQuery<Schema<'AdminMemberPage'>>(
    '/api/v1/administrative/members',
    {},
    {},
    me.allowedActions.includes('members.read'),
  );
  const [quickProvider, setQuickProvider] = useState(false);
  const [providerId, setProviderId] = useState(resource?.providerAccountId || '');
  const [duplicatePreview, setDuplicatePreview] = useState<{
    identity: string;
    result: Schema<'AdminDuplicatePreview'>;
  } | null>(null);
  const duplicateIdentity = JSON.stringify([
    sheetId,
    sheet?.kind,
    metadata.domain,
    metadata.host,
    metadata.port,
    metadata.protocol || 'HTTP',
  ]);
  const duplicateWarning =
    duplicatePreview?.identity === duplicateIdentity ? duplicatePreview : null;
  const stale = Boolean(
    resource &&
    (latestResource?.version !== resource.version ||
      latestResource?.capabilities.financeRead !== resource.capabilities.financeRead ||
      (action.error instanceof ApiError && action.error.status === 409)),
  );
  const dictionaries = (kind: string) => me.dictionaries.filter((item) => item.kind === kind);
  return (
    <AdminForm
      title={resource ? `Изменить: ${resource.name}` : 'Добавить ресурс'}
      action={action}
      cancel={resource ? `resources/${resource.id}` : sheetId ? `sheets/${sheetId}` : 'resources'}
      label={resource ? 'Сохранить' : duplicateWarning ? 'Создать всё равно' : 'Создать'}
      submit={async (data) => {
        if (!sheet) throw new Error('Выберите лист');
        const submittedMetadata = {
          ...metadata,
          ...(metadata.ip !== undefined ? { ip: metadata.ip.trim() } : {}),
          ...(metadata.domain !== undefined ? { domain: metadata.domain.trim() } : {}),
          ...(metadata.host !== undefined ? { host: metadata.host.trim() } : {}),
        };
        if (sheet.kind === 'SERVER' && !validIPAddress(submittedMetadata.ip || ''))
          throw new Error('Укажите IP-адрес сервера в формате IPv4 или IPv6, без доменного имени.');
        if (sheet.kind === 'DOMAIN' && !validResourceHost(submittedMetadata.domain || ''))
          throw new Error('Укажите домен без протокола, логина и пути.');
        if (sheet.kind === 'SERVICE' && !submittedMetadata.serviceTypeId)
          throw new Error('Выберите тип услуги.');
        if (
          sheet.kind === 'PROXY' &&
          (!validResourceHost(submittedMetadata.host || '') ||
            !Number.isInteger(submittedMetadata.port) ||
            !submittedMetadata.port ||
            submittedMetadata.port < 1 ||
            submittedMetadata.port > 65535)
        )
          throw new Error('Укажите хост прокси без протокола и логина и целый порт от 1 до 65535.');
        const hasCredentialInput = login.length > 0 || password.length > 0;
        if (
          resource &&
          caps.connectionEdit &&
          replaceCredentials &&
          !clearCredentials &&
          !hasCredentialInput
        )
          throw new Error(
            'Укажите логин или пароль либо снимите замену. Для удаления отметьте отдельный пункт.',
          );
        const body: Omit<Schema<'AdminResourcePatch'>, 'version'> & { version?: number } = {
          sheetId,
          name: text(data, 'name'),
          metadata:
            sheet.kind === 'PROXY' ? { protocol: 'HTTP', ...submittedMetadata } : submittedMetadata,
          customValues: Object.fromEntries(
            sheet.customColumns.map((column) => [column.key, text(data, `custom:${column.key}`)]),
          ),
          projectId: nullable(data, 'projectId'),
          providerAccountId: providerId || null,
          paymentPeriodId: nullable(data, 'paymentPeriodId'),
          paidUntil: nullable(data, 'paidUntil'),
          statusId: nullable(data, 'statusId'),
          relevanceId: nullable(data, 'relevanceId'),
          comment: nullable(data, 'comment'),
          autoRenew: data.get('autoRenew') === 'on',
          ...(!resource ? { priceKind: 'UNKNOWN' as const } : {}),
          ...(data.has('responsibleAccountId')
            ? { responsibleAccountId: nullable(data, 'responsibleAccountId') }
            : {}),
          ...(editFinance
            ? {
                priceKind,
                cost:
                  priceKind === 'PAID'
                    ? normalizeAdministrativeMoney(text(data, 'cost'))
                    : priceKind === 'FREE'
                      ? '0.00'
                      : null,
                currency:
                  priceKind === 'UNKNOWN'
                    ? null
                    : (text(data, 'currency') as 'USD' | 'EUR' | 'RUB'),
                paymentMethodId: nullable(data, 'paymentMethodId'),
              }
            : {}),
          ...(caps.connectionEdit && clearCredentials
            ? { credentials: null }
            : caps.connectionEdit && replaceCredentials && hasCredentialInput
              ? { credentials: { login, password } }
              : {}),
          ...(resource ? { version: resource.version } : {}),
        };
        if (!resource && ['DOMAIN', 'PROXY'].includes(sheet.kind) && !duplicateWarning) {
          const result = await action.run<Schema<'AdminDuplicatePreview'>>(
            'POST',
            '/api/v1/administrative/resources/duplicate-preview',
            body,
            {},
            '',
          );
          if (!result) return;
          if (result.duplicates.length || result.warnings.length) {
            setDuplicatePreview({ identity: duplicateIdentity, result });
            return;
          }
        }
        const result = await action.run<Resource>(
          resource ? 'PATCH' : 'POST',
          resource ? '/api/v1/administrative/resources/{id}' : '/api/v1/administrative/resources',
          body,
          resource ? { id: resource.id } : {},
        );
        if (result) {
          setLogin('');
          setPassword('');
          setProxyInput('');
          await onDone(result);
        }
      }}
    >
      {stale && (
        <div className="admin-overdue" role="status">
          <p>Запись или доступ к её данным изменились. Ваши правки сохранены.</p>
          <Button disabled={action.pending} onClick={() => void onReload()}>
            Загрузить актуальную версию и сбросить правки
          </Button>
        </div>
      )}
      {duplicateWarning && (
        <div className="admin-overdue" role="status">
          <p>Найдены похожие ресурсы. Проверьте их перед созданием ещё одной записи.</p>
          {duplicateWarning.result.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
          {duplicateWarning.result.duplicates.map((item) => (
            <p key={item.id}>
              <AdminLink path={`resources/${item.id}`}>{item.name}</AdminLink>
            </p>
          ))}
        </div>
      )}
      <AdminSelect
        label="Лист"
        name="sheetId"
        options={me.sheets
          .filter(
            (item) =>
              (!resource || item.kind === resource.kind) &&
              (item.id === resource?.sheetId ||
                item.creationAllowedActions.includes('resource.create')),
          )
          .map((item) => ({
            ...item,
            name: `${me.sections.find((section) => section.id === item.sectionId)?.name || 'Раздел'} · ${item.name}`,
          }))}
        value={sheetId}
        onChange={(value) => {
          setSheetId(value);
          if (!resource) setMetadata({});
        }}
        required
      />
      <Field label="Название" name="name" required defaultValue={resource?.name} maxLength={200} />
      {sheet?.kind === 'PROXY' && (
        <>
          <Field label="Вставить строку прокси">
            <textarea
              aria-label="Строка прокси"
              value={proxyInput}
              onChange={(e) => setProxyInput(e.target.value)}
              placeholder="host:port:login:password или socks5://login:password@host:port"
            />
          </Field>
          <Button
            onClick={() => {
              try {
                const parsed = parseAdministrativeProxy(proxyInput);
                setMetadata({
                  ...metadata,
                  host: parsed.host,
                  port: parsed.port,
                  protocol: parsed.protocol,
                });
                if (caps.connectionEdit) {
                  setLogin(parsed.login || '');
                  setPassword(parsed.password || '');
                  setReplaceCredentials(true);
                  setClearCredentials(false);
                }
                setProxyError(null);
              } catch (error) {
                setProxyError(error as Error);
              }
            }}
          >
            Разобрать строку
          </Button>
          {proxyError && <ErrorState error={proxyError} />}
          <p className="muted">Проверьте разобранные поля перед сохранением.</p>
          <Field
            label="Хост"
            value={metadata.host || ''}
            onChange={(e) => setMetadata({ ...metadata, host: e.target.value })}
            required
          />
          <Field
            label="Порт"
            type="number"
            min={1}
            max={65535}
            value={metadata.port || ''}
            onChange={(e) => setMetadata({ ...metadata, port: Number(e.target.value) })}
            required
          />
          <AdminSelect
            label="Протокол"
            options={['HTTP', 'HTTPS', 'SOCKS5'].map((id) => ({ id, name: id }))}
            value={metadata.protocol || 'HTTP'}
            onChange={(value) =>
              setMetadata({
                ...metadata,
                protocol: ['HTTP', 'HTTPS', 'SOCKS5'].includes(value)
                  ? (value as 'HTTP' | 'HTTPS' | 'SOCKS5')
                  : 'HTTP',
              })
            }
            required
          />
          <Field
            label="География"
            value={metadata.geo || ''}
            onChange={(e) => setMetadata({ ...metadata, geo: e.target.value })}
          />
          <Field
            label="Назначение"
            value={metadata.assignment || ''}
            onChange={(e) => setMetadata({ ...metadata, assignment: e.target.value })}
          />
        </>
      )}
      {sheet?.kind === 'SERVER' && (
        <>
          <Field
            label="IP-адрес сервера"
            value={metadata.ip || ''}
            onChange={(e) => setMetadata({ ...metadata, ip: e.target.value })}
            required
            placeholder="192.0.2.10 или 2001:db8::10"
          />
          <Field
            label="Операционная система"
            value={metadata.os || ''}
            onChange={(e) => setMetadata({ ...metadata, os: e.target.value })}
          />
        </>
      )}
      {sheet?.kind === 'DOMAIN' && (
        <Field
          label="Домен"
          value={metadata.domain || ''}
          onChange={(e) => setMetadata({ ...metadata, domain: e.target.value })}
          required
        />
      )}
      {sheet?.kind === 'SERVICE' && (
        <AdminSelect
          label="Тип сервиса"
          options={dictionaries('SERVICE_TYPE')}
          value={metadata.serviceTypeId || ''}
          onChange={(value) => setMetadata(value ? { ...metadata, serviceTypeId: value } : {})}
          required
        />
      )}
      <AdminSelect
        label="Проект"
        name="projectId"
        options={me.projects}
        value={resource?.projectId || undefined}
      />
      <AdminSelect
        label="Провайдер"
        options={me.providers}
        value={providerId}
        onChange={setProviderId}
      />
      {me.allowedActions.includes('provider.edit') && (
        <Button onClick={() => setQuickProvider(true)}>Создать провайдера</Button>
      )}
      {quickProvider && (
        <QuickProvider
          onClose={() => setQuickProvider(false)}
          onDone={(id) => {
            setProviderId(id);
            setQuickProvider(false);
          }}
        />
      )}
      <AdminSelect
        label="Рабочий статус"
        name="statusId"
        options={dictionaries('STATUS')}
        value={resource?.statusId || undefined}
      />
      <AdminSelect
        label="Актуальность"
        name="relevanceId"
        options={dictionaries('RELEVANCE')}
        value={resource?.relevanceId || undefined}
      />
      {members.data && (
        <AdminSelect
          label="Ответственный"
          name="responsibleAccountId"
          options={members.data.items.map((item) => ({ id: item.accountId, name: item.login }))}
          value={resource?.responsibleAccountId || undefined}
        />
      )}
      <h2>Срок и период</h2>
      <AdminSelect
        label="Период оплаты"
        name="paymentPeriodId"
        options={dictionaries('PERIOD')}
        value={resource?.paymentPeriodId || undefined}
      />
      <Field
        label="Оплачен до"
        name="paidUntil"
        type="date"
        defaultValue={resource?.paidUntil || ''}
      />
      {caps.financeEdit && resource && !readableFinance && (
        <>
          <p className="muted">Текущие сумма, валюта и способ оплаты здесь не показаны.</p>
          <label className="admin-check">
            <input
              type="checkbox"
              checked={replaceFinance}
              onChange={(event) => {
                setReplaceFinance(event.target.checked);
                setPriceKind('UNKNOWN');
              }}
            />
            Задать новые финансовые данные
          </label>
        </>
      )}
      {editFinance && (
        <>
          <h2>Оплата</h2>
          <AdminSelect
            label="Стоимость"
            options={[
              { id: 'PAID', name: 'Платный' },
              { id: 'FREE', name: 'Бесплатный' },
              { id: 'UNKNOWN', name: 'Неизвестна' },
            ]}
            value={priceKind}
            onChange={(value) =>
              setPriceKind(value === 'PAID' || value === 'FREE' ? value : 'UNKNOWN')
            }
            required
          />
          {priceKind === 'PAID' && (
            <Field
              label="Стоимость за период"
              name="cost"
              inputMode="decimal"
              required
              defaultValue={readableFinance ? resource?.cost || '' : ''}
            />
          )}
          {priceKind !== 'UNKNOWN' && (
            <AdminSelect
              label="Валюта"
              name="currency"
              options={['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id }))}
              value={readableFinance ? resource?.currency || 'USD' : 'USD'}
              required
            />
          )}
          <AdminSelect
            label="Способ оплаты"
            name="paymentMethodId"
            options={dictionaries('METHOD')}
            value={readableFinance ? resource?.paymentMethodId || undefined : undefined}
          />
        </>
      )}
      {caps.connectionEdit && (
        <>
          <h2>Данные подключения</h2>
          {resource && (
            <label className="admin-check">
              <input
                type="checkbox"
                checked={replaceCredentials}
                onChange={(e) => {
                  setReplaceCredentials(e.target.checked);
                  if (e.target.checked) setClearCredentials(false);
                }}
              />
              Заменить данные подключения
            </label>
          )}
          {resource?.hasCredentials && (
            <label className="admin-check">
              <input
                type="checkbox"
                checked={clearCredentials}
                onChange={(event) => {
                  setClearCredentials(event.target.checked);
                  if (event.target.checked) {
                    setReplaceCredentials(false);
                    setLogin('');
                    setPassword('');
                    setProxyInput('');
                  }
                }}
              />
              Удалить сохранённые данные подключения
            </label>
          )}
          {replaceCredentials && (
            <>
              <Field
                label="Логин подключения"
                autoComplete="off"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
              />
              <Field
                label="Пароль подключения"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}
        </>
      )}
      {sheet?.customColumns.map((column) => (
        <Field
          key={column.key}
          label={column.name}
          name={`custom:${column.key}`}
          defaultValue={resource?.customValues[column.key]}
        />
      ))}
      <label className="admin-check">
        <input name="autoRenew" type="checkbox" defaultChecked={resource?.autoRenew} />
        Автопродление у провайдера включено
      </label>
      <Field label="Комментарий">
        <textarea name="comment" maxLength={10000} defaultValue={resource?.comment || ''} />
      </Field>
    </AdminForm>
  );
}
function QuickProvider({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  return (
    <AdminConfirm
      title="Создать провайдера"
      action={action}
      onClose={onClose}
      onConfirm={async () => {
        const result = await action.run<Schema<'AdminProvider'>>(
          'POST',
          '/api/v1/administrative/providers',
          { name, serviceUrl: url || null },
        );
        if (result) onDone(result.id);
      }}
    >
      <Field label="Название" value={name} required onChange={(e) => setName(e.target.value)} />
      <Field label="Сайт" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      {me.providers.some((item) => item.name.toLowerCase() === name.toLowerCase()) && (
        <p className="admin-overdue">
          Провайдер с таким названием уже есть. Проверьте, нужен ли ещё один аккаунт.
        </p>
      )}
    </AdminConfirm>
  );
}
export function AdministrativeResourceDetail({
  id,
  initialTab = 'overview',
}: {
  id: string;
  initialTab?: string;
}) {
  const me = useAdministrative();
  const query = useAdminQuery<Resource>('/api/v1/administrative/resources/{id}', { id });
  const row = query.data;
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const [tab, setTab] = useState(initialTab === 'links' ? 'links' : 'overview');
  const [lifecycle, setLifecycle] = useState<keyof typeof lifecyclePaths | null>(null);
  const [credentials, setCredentials] = useState<{
    resourceId: string;
    value: Schema<'AdminCredentials'>;
  } | null>(null);
  const credentialRequest = useRef<AbortController | null>(null);
  const credentialGeneration = useRef(0);
  const hideCredentials = useCallback(() => {
    credentialGeneration.current += 1;
    credentialRequest.current?.abort();
    credentialRequest.current = null;
    setCredentials(null);
  }, []);
  const visibleCredentials = credentials?.resourceId === id ? credentials.value : null;
  const [replacementSheet, setReplacementSheet] = useState('');
  const [replacementProject, setReplacementProject] = useState('KEEP');
  const [replacementProvider, setReplacementProvider] = useState('KEEP');
  const payments = useAdminQuery<Schema<'AdminPaymentPage'>>(
    '/api/v1/administrative/resources/{id}/payments',
    { id },
    {},
    Boolean(row?.capabilities.financeRead && tab === 'payments'),
  );
  const [linkSearch, setLinkSearch] = useState('');
  useEffect(() => {
    const hide = hideCredentials;
    window.addEventListener('blur', hide);
    const timer = setTimeout(hide, 60_000);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('blur', hide);
    };
  }, [credentials, hideCredentials]);
  useEffect(() => {
    if (query.error || !row?.capabilities.connectionRead) hideCredentials();
  }, [query.error, row?.capabilities.connectionRead, credentials, hideCredentials]);
  useEffect(() => {
    hideCredentials();
    return () => {
      credentialGeneration.current += 1;
      credentialRequest.current?.abort();
    };
  }, [id, hideCredentials]);
  if (query.error instanceof ApiError && [403, 404].includes(query.error.status))
    return <QueryState query={query} />;
  if (!row) return <QueryState query={query} />;
  const can = (permission: string) => row.allowedActions.includes(permission);
  const lifecycleLabel = {
    archive: 'Архивировать',
    unarchive: 'Вернуть из архива',
    trash: 'Переместить в корзину',
    restore: 'Восстановить',
    purge: 'Удалить окончательно',
  };
  return (
    <>
      <header className="admin-heading">
        <div>
          <h1>{row.name}</h1>
          <p className="muted">
            {kindLabels[row.kind]} · {expiryLabel(row, today(me))}
          </p>
        </div>
        <div className="admin-toolbar">
          {!row.deletedAt && can('resource.edit') && (
            <AdminLink className="pill" path={`resources/${id}/edit`}>
              Изменить
            </AdminLink>
          )}
          {!row.deletedAt && row.capabilities.financeEdit && row.capabilities.financeRead && (
            <AdminLink className="pill" path={`resources/${id}/renew`}>
              Продлить
            </AdminLink>
          )}
          {!row.deletedAt && can('resource.archive') && (
            <Button onClick={() => setLifecycle(row.archivedAt ? 'unarchive' : 'archive')}>
              {row.archivedAt ? 'Вернуть из архива' : 'Архивировать'}
            </Button>
          )}
          {!row.deletedAt && can('resource.delete') && (
            <Button onClick={() => setLifecycle('trash')}>В корзину</Button>
          )}
          {row.deletedAt && can('trash.restore') && (
            <Button onClick={() => setLifecycle('restore')}>Восстановить</Button>
          )}
          {row.deletedAt && can('trash.purge') && (
            <Button onClick={() => setLifecycle('purge')}>Удалить окончательно</Button>
          )}
          {administrativeIntegrationsReady &&
            row.kind === 'PROXY' &&
            !row.deletedAt &&
            can('proxy.check') && (
              <Button
                disabled={action.pending}
                onClick={async () => {
                  const job = await action.run<Schema<'AdminIntegrationJob'>>(
                    'POST',
                    '/api/v1/administrative/integrations/proxy-checks',
                    { resourceIds: [id], operationKey: crypto.randomUUID() },
                    {},
                    'Проверка поставлена в очередь',
                  );
                  if (job) await navigate(`jobs/${job.id}`);
                }}
              >
                Проверить прокси
              </Button>
            )}
        </div>
      </header>
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: 'Сведения' },
          ...(row.capabilities.financeRead ? [{ value: 'payments', label: 'История оплат' }] : []),
          { value: 'links', label: 'Связанные ресурсы' },
        ]}
      />
      {action.error && !lifecycle && <ErrorState error={action.error} />}
      {tab === 'overview' && (
        <>
          <section className="panel">
            <InfoRows
              rows={[
                [
                  'Лист',
                  <AdminLink path={`sheets/${row.sheetId}`}>
                    {me.sheets.find((item) => item.id === row.sheetId)?.name || 'Открыть'}
                  </AdminLink>,
                ],
                ['Проект', me.projects.find((item) => item.id === row.projectId)?.name],
                [
                  'Провайдер',
                  row.providerAccountId ? (
                    <AdminLink path={`providers/${row.providerAccountId}`}>
                      {me.providers.find((item) => item.id === row.providerAccountId)?.name ||
                        'Открыть'}
                    </AdminLink>
                  ) : null,
                ],
                ['Рабочий статус', me.dictionaries.find((item) => item.id === row.statusId)?.name],
                ['Актуальность', me.dictionaries.find((item) => item.id === row.relevanceId)?.name],
                ['Оплачен до', dateLabel(row.paidUntil)],
                ['Период', me.dictionaries.find((item) => item.id === row.paymentPeriodId)?.name],
                ...Object.entries(row.metadata).map(([key, value]): [string, string] => [
                  (
                    {
                      ip: 'IP / адрес',
                      os: 'ОС',
                      domain: 'Домен',
                      serviceTypeId: 'Тип сервиса',
                      host: 'Хост',
                      port: 'Порт',
                      protocol: 'Протокол',
                      geo: 'География',
                      assignment: 'Назначение',
                    } as Record<string, string>
                  )[key] || key,
                  key === 'serviceTypeId'
                    ? me.dictionaries.find((item) => item.id === value)?.name || 'Не указан'
                    : String(value),
                ]),
                ...Object.entries(row.customValues).map(([key, value]): [string, string] => [
                  me.sheets
                    .find((item) => item.id === row.sheetId)
                    ?.customColumns.find((column) => column.key === key)?.name || key,
                  value,
                ]),
                ['Автопродление у провайдера', row.autoRenew ? 'Включено' : 'Выключено'],
                ['Комментарий', row.comment],
                ['Создан', dateTimeLabel(row.createdAt, me.timezone)],
                ...(row.deletedAt
                  ? ([
                      ['Удалён', dateTimeLabel(row.deletedAt, me.timezone)],
                      ['Кем удалён', row.deletedByAccountId],
                    ] as [string, string | null][])
                  : []),
              ]}
            />
          </section>
          {row.capabilities.financeRead && (
            <section className="panel">
              <h2>Оплата</h2>
              <InfoRows
                rows={[
                  [
                    'Стоимость за период',
                    row.priceKind === 'FREE' ? 'Бесплатно' : moneyLabel(row.cost, row.currency),
                  ],
                  [
                    'Способ оплаты',
                    me.dictionaries.find((item) => item.id === row.paymentMethodId)?.name,
                  ],
                ]}
              />
            </section>
          )}
          {row.hasCredentials && row.capabilities.connectionRead && (
            <section className="panel">
              <h2>Данные подключения</h2>
              {visibleCredentials ? (
                <>
                  <InfoRows
                    rows={[
                      ['Логин', visibleCredentials.login],
                      [
                        'Пароль',
                        <code className="admin-secret">{visibleCredentials.password}</code>,
                      ],
                    ]}
                  />
                  <Button onClick={hideCredentials}>Скрыть</Button>
                </>
              ) : (
                <Button
                  disabled={action.pending}
                  onClick={async () => {
                    hideCredentials();
                    const request = new AbortController();
                    credentialRequest.current = request;
                    const generation = credentialGeneration.current;
                    try {
                      const value = await getObject<Schema<'AdminCredentials'>>(
                        '/api/v1/administrative/resources/{id}/credentials',
                        { id },
                        request.signal,
                      );
                      if (
                        !request.signal.aborted &&
                        generation === credentialGeneration.current &&
                        document.visibilityState !== 'hidden'
                      )
                        setCredentials({ resourceId: id, value });
                    } catch (error) {
                      if (!request.signal.aborted) action.setError(error as Error);
                    } finally {
                      if (credentialRequest.current === request) credentialRequest.current = null;
                    }
                  }}
                >
                  Показать данные подключения
                </Button>
              )}
              <p className="muted">Данные скрываются через минуту и при уходе из окна.</p>
            </section>
          )}
          {row.proxyCheck && (
            <section className="panel">
              <h2>Последняя проверка прокси</h2>
              <InfoRows
                rows={[
                  [
                    'Результат',
                    row.proxyCheck.status === 'AVAILABLE'
                      ? 'Работает'
                      : row.proxyCheck.status === 'UNAVAILABLE'
                        ? 'Недоступен'
                        : row.proxyCheck.status,
                  ],
                  ['Дата', dateTimeLabel(row.proxyCheck.checkedAt, me.timezone)],
                  ['Внешний IP', row.proxyCheck.exitIp],
                  ['География', row.proxyCheck.geo],
                  [
                    'Задержка',
                    row.proxyCheck.latencyMs == null
                      ? 'Не определена'
                      : `${row.proxyCheck.latencyMs} мс`,
                  ],
                  ['Ошибка', row.proxyCheck.error],
                ]}
              />
            </section>
          )}
        </>
      )}
      {tab === 'payments' && row.capabilities.financeRead && (
        <>
          <QueryState query={payments} />
          <PaymentTable rows={payments.data?.items || []} />
        </>
      )}
      {tab === 'links' && (
        <AdministrativeList
          count={row.links.length}
          toolbar={
            <>
              <Search value={linkSearch} onChange={setLinkSearch} placeholder="Поиск связей" />
              <span className="toolbar-spacer" />
              {can('resource.edit') && (
                <AdminLink className="pill" path={`resources/${id}/link`}>
                  <Icon name="Plus" size={16} />
                  Добавить связь
                </AdminLink>
              )}
            </>
          }
        >
          <Table
            rows={row.links.filter(
              (item) =>
                !linkSearch ||
                item.label.toLocaleLowerCase('ru').includes(linkSearch.toLocaleLowerCase('ru')),
            )}
            columns={[
              {
                key: 'label',
                label: 'Связь',
                render: (link) => (
                  <AdminLink path={`resources/${link.targetResourceId}`}>{link.label}</AdminLink>
                ),
              },
              {
                key: 'remove',
                label: '',
                filter: false,
                sortable: false,
                render: (link) =>
                  can('resource.edit') ? (
                    <Button
                      disabled={action.pending}
                      onClick={() =>
                        void action.run(
                          'DELETE',
                          '/api/v1/administrative/resources/{id}/links/{linkId}',
                          { version: row.version },
                          { id, linkId: link.id },
                          'Связь удалена',
                        )
                      }
                    >
                      Убрать
                    </Button>
                  ) : null,
              },
            ]}
          />
        </AdministrativeList>
      )}
      {lifecycle && (
        <AdminConfirm
          title={`${lifecycleLabel[lifecycle]}?`}
          action={action}
          onClose={() => setLifecycle(null)}
          onConfirm={async () => {
            if (
              await action.run(
                'POST',
                lifecyclePaths[lifecycle],
                {
                  version: row.version,
                  ...(lifecycle === 'restore' && replacementSheet
                    ? { sheetId: replacementSheet }
                    : {}),
                  ...(lifecycle === 'restore' && replacementProject !== 'KEEP'
                    ? { projectId: replacementProject === 'CLEAR' ? null : replacementProject }
                    : {}),
                  ...(lifecycle === 'restore' && replacementProvider !== 'KEEP'
                    ? {
                        providerAccountId:
                          replacementProvider === 'CLEAR' ? null : replacementProvider,
                      }
                    : {}),
                },
                { id },
                'Операция выполнена',
              )
            ) {
              setLifecycle(null);
              hideCredentials();
              if (lifecycle === 'purge') await navigate('trash');
            }
          }}
        >
          <p>
            {lifecycle === 'purge'
              ? 'Данные ресурса и подключения будут удалены без возможности восстановления. История оплат останется.'
              : lifecycle === 'trash'
                ? 'Запись можно восстановить из корзины в течение 30 дней.'
                : 'Сохранённые оплаты остаются в истории.'}
          </p>
          {lifecycle === 'restore' && (
            <>
              <p>
                Если прежний лист, проект или провайдер недоступен, выберите замену. Проект и
                провайдера можно убрать.
              </p>
              <AdminSelect
                label="Лист для восстановления (если прежний удалён)"
                options={me.sheets
                  .filter(
                    (item) =>
                      item.kind === row.kind &&
                      item.creationAllowedActions.includes('resource.create'),
                  )
                  .map((item) => ({
                    ...item,
                    name: `${me.sections.find((section) => section.id === item.sectionId)?.name || 'Раздел'} · ${item.name}`,
                  }))}
                value={replacementSheet}
                onChange={setReplacementSheet}
              />
              <AdminSelect
                label="Проект при восстановлении"
                options={[
                  { id: 'KEEP', name: 'Оставить прежний проект' },
                  { id: 'CLEAR', name: 'Убрать связь с проектом' },
                  ...me.projects,
                ]}
                value={replacementProject}
                onChange={(value) => setReplacementProject(value || 'KEEP')}
              />
              <AdminSelect
                label="Провайдер при восстановлении"
                options={[
                  { id: 'KEEP', name: 'Оставить прежнего провайдера' },
                  { id: 'CLEAR', name: 'Убрать связь с провайдером' },
                  ...me.providers,
                ]}
                value={replacementProvider}
                onChange={(value) => setReplacementProvider(value || 'KEEP')}
              />
            </>
          )}
        </AdminConfirm>
      )}
    </>
  );
}
export function AdministrativeResourceLink({ id }: { id: string }) {
  const query = useAdminQuery<Resource>('/api/v1/administrative/resources/{id}', { id });
  const action = useAdminAction();
  const [reset, setReset] = useState(0);
  if (query.error instanceof ApiError && [403, 404].includes(query.error.status))
    return <QueryState query={query} />;
  if (!query.data) return <QueryState query={query} />;
  if (!query.data.allowedActions.includes('resource.edit') || query.data.deletedAt)
    return <Empty title="Добавление связи недоступно" />;
  return (
    <ResourceLinkEditor
      key={`${id}:${reset}`}
      resource={query.data}
      action={action}
      onReload={async () => {
        const result = await query.refetch();
        if (result.error || !result.data) return;
        action.setError(null);
        setReset((previous) => previous + 1);
      }}
    />
  );
}
function ResourceLinkEditor({
  resource: latest,
  action,
  onReload,
}: {
  resource: Resource;
  action: ReturnType<typeof useAdminAction>;
  onReload: () => Promise<void>;
}) {
  const [resource] = useState(latest);
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState('');
  const navigate = useAdminNavigate();
  const candidates = useAdminList<Resource>('/api/v1/administrative/resources', {
    search,
    state: 'ALL',
  });
  const stale =
    latest.version !== resource.version ||
    (action.error instanceof ApiError && action.error.status === 409);
  return (
    <AdminForm
      title={`Добавить связь: ${resource.name}`}
      action={action}
      cancel={`resources/${resource.id}?tab=links`}
      label="Добавить связь"
      submit={async (data) => {
        if (!target) throw new Error('Выберите связанный ресурс.');
        const body: Schema<'AdminLinkCreate'> = {
          version: resource.version,
          targetResourceId: target,
          label: text(data, 'label'),
        };
        if (
          await action.run(
            'POST',
            '/api/v1/administrative/resources/{id}/links',
            body,
            { id: resource.id },
            'Связь добавлена',
          )
        )
          await navigate(`resources/${resource.id}?tab=links`);
      }}
    >
      {stale && (
        <div className="admin-overdue" role="status">
          <p>Запись изменилась. Название связи и выбранный ресурс сохранены.</p>
          <Button disabled={action.pending} onClick={() => void onReload()}>
            Загрузить актуальную версию и сбросить правки
          </Button>
        </div>
      )}
      <Field
        label="Поиск связанного ресурса"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <QueryState query={candidates} />
      <AdminSelect
        label="Связанный ресурс"
        options={candidates.items.filter((item) => item.id !== resource.id && !item.deletedAt)}
        value={target}
        onChange={setTarget}
        required
      />
      <Field label="Название связи" name="label" required maxLength={200} />
    </AdminForm>
  );
}
export function AdministrativeRenewal({ id }: { id: string }) {
  const me = useAdministrative();
  const action = useAdminAction();
  const navigate = useAdminNavigate();
  const resource = useAdminQuery<Resource>('/api/v1/administrative/resources/{id}', { id });
  const [preview, setPreview] = useState<Schema<'AdminRenewalPreview'> | null>(null);
  useEffect(() => {
    if (resource.data)
      void action
        .run<Schema<'AdminRenewalPreview'>>(
          'POST',
          '/api/v1/administrative/resources/{id}/renew-preview',
          { version: resource.data.version },
          { id },
          '',
        )
        .then((value) => {
          if (value) setPreview(value);
        });
  }, [resource.data?.version]);
  if (!resource.data) return <QueryState query={resource} />;
  if (!preview)
    return action.error ? (
      <ErrorState error={action.error} />
    ) : (
      <Empty title="Расчёт срока продления…" />
    );
  return (
    <AdminForm
      title={`Продлить: ${resource.data.name}`}
      action={action}
      cancel={`resources/${id}`}
      label="Записать оплату и продлить"
      submit={async (data) => {
        if (
          await action.run(
            'POST',
            '/api/v1/administrative/resources/{id}/renew',
            {
              version: resource.data!.version,
              paymentDate: text(data, 'paymentDate'),
              paidUntil: text(data, 'paidUntil'),
              amount: normalizeAdministrativeMoney(text(data, 'amount')),
              currency: text(data, 'currency'),
              paymentMethodId: nullable(data, 'paymentMethodId'),
              comment: nullable(data, 'comment'),
              operationKey: crypto.randomUUID(),
            },
            { id },
            'Оплата записана, срок обновлён',
          )
        )
          await navigate(`resources/${id}`);
      }}
    >
      <p className="muted">
        Предыдущий срок: {dateLabel(preview.previousPaidUntil)}. Запишите фактическую оплату и
        проверьте новый срок.
      </p>
      <Field
        label="Дата фактической оплаты"
        name="paymentDate"
        type="date"
        required
        defaultValue={today(me)}
      />
      <Field
        label="Фактическая сумма"
        name="amount"
        inputMode="decimal"
        required
        defaultValue={preview.amount || ''}
      />
      <AdminSelect
        label="Валюта"
        name="currency"
        options={['USD', 'EUR', 'RUB'].map((id) => ({ id, name: id }))}
        value={preview.currency || 'USD'}
        required
      />
      <Field
        label="Новый срок"
        name="paidUntil"
        type="date"
        required
        defaultValue={preview.suggestedPaidUntil || ''}
      />
      <AdminSelect
        label="Способ оплаты"
        name="paymentMethodId"
        options={me.dictionaries.filter((item) => item.kind === 'METHOD')}
        value={resource.data.paymentMethodId || undefined}
      />
      <Field label="Комментарий">
        <textarea name="comment" maxLength={2000} />
      </Field>
    </AdminForm>
  );
}
export function PaymentTable({ rows }: { rows: Schema<'AdminPaymentPage'>['items'] }) {
  return (
    <Table
      rows={rows}
      columns={[
        { key: 'paymentDate', label: 'Дата оплаты', render: (row) => dateLabel(row.paymentDate) },
        {
          key: 'resourceId',
          label: 'Ресурс',
          render: (row) => (
            <AdminLink path={`resources/${row.resourceId}`}>Открыть ресурс</AdminLink>
          ),
        },
        {
          key: 'amount',
          label: 'Фактическая сумма',
          render: (row) => moneyLabel(row.amount, row.currency),
        },
        { key: 'paidUntil', label: 'Оплачен до', render: (row) => dateLabel(row.paidUntil) },
        { key: 'comment', label: 'Комментарий' },
      ]}
    />
  );
}
export function AdministrativePayments() {
  const query = useAdminQuery<Schema<'AdminPaymentPage'>>('/api/v1/administrative/payments');
  return (
    <AdministrativeList
      count={query.data?.items.length}
      className="admin-payment-list"
      toolbar={
        <>
          <span className="muted">Фактические оплаты</span>
          <span className="toolbar-spacer" />
          <AdminLink className="pill" path="calendar">
            Календарь платежей
          </AdminLink>
        </>
      }
    >
      <QueryState query={query} />
      <PaymentTable rows={query.data?.items || []} />
    </AdministrativeList>
  );
}
